import BaseTableNew from 'components/atoms/base-table/new';
import useUser from 'utils/useUser';
import { usePatientLatestReading } from 'infrastructure/hooks/rpm/overview-dashboard/use-patient-latest-reading';
import { useMemo, type FC } from 'react';
import { Routes } from 'router/routes';

import { useColumns } from './columns';
import s from './styles.module.scss';
import MobileTable from './mobile-table';

import type { IAssignTool } from 'infrastructure/interfaces';

interface AssignToolTableProps {
  patients?: IAssignTool[];
  isLoading: boolean;
  toggleOpen: () => void;
}

const AssignToolTable: FC<AssignToolTableProps> = ({
  patients,
  isLoading,
  toggleOpen,
}) => {
  const patientGuids = useMemo(() => {
    if (!patients || patients.length === 0) {
      return [];
    }
    return patients.map((patient) => patient.guid);
  }, [patients]);

  const {
    latestReadings,
    isLoading: isLoadingReadings,
    isFetching: isFetchingReadings,
  } = usePatientLatestReading({
    patientGuids,
  });

  const assignedWithLatestReadings = useMemo(() => {
    if (!patients || patients.length === 0) {
      return [];
    }

    return patients.map((patient) => {
      const latestReading = latestReadings.find(
        (reading) => reading.patientGuid === patient.guid,
      );

      return {
        ...patient,
        latestReading: latestReading || null,
      };
    });
  }, [patients, latestReadings]);

  const columns = useColumns(
    toggleOpen,
    isLoadingReadings || isFetchingReadings,
  );
  const { isMobileDevice } = useUser();

  if (isMobileDevice) {
    return (
      <MobileTable
        patients={patients}
        isLoading={isLoading}
        toggleOpen={toggleOpen}
      />
    );
  }

  return (
    <BaseTableNew
      rowKey="guid"
      columns={columns}
      items={assignedWithLatestReadings ?? []}
      bordered={false}
      loading={isLoading}
      className={s.table}
      dataCy="assign-tool-table"
      showHeader={false}
      changeOddRowColor={false}
      onRow={(record) => ({
        onClick: (e) => {
          const target = e.target as HTMLElement;
          if (
            target.closest('a') ||
            target.closest('button') ||
            target.closest('[role="button"]') ||
            target.closest('input') ||
            target.closest('select') ||
            target.closest('textarea')
          ) {
            return;
          }

          window.open(Routes.patientGuid(record.guid), '_blank');
          toggleOpen();
        },
      })}
    />
  );
};
export default AssignToolTable;
